import { ethers } from 'ethers';
import MethaTokenArtifact from './abis/MethaToken.json';
import MethaCrowdsaleArtifact from './abis/MethaCrowdsale.json';
import OpenGovernanceArtifact from './abis/OpenGovernance.json';
import AuditingPoolArtifact from './abis/AuditingPool.json';
import EternalStorageArtifact from './abis/EternalStorage.json';
import AuditorRegistryV2Artifact from './abis/AuditorRegistryV2.json';
import { ADDRESSES, CHAIN_ID } from './chain';

export type MethaContracts = {
  token: ethers.Contract;
  crowdsale: ethers.Contract;
  governance: ethers.Contract;
  auditingPool: ethers.Contract;
  registry: ethers.Contract;
  storage: ethers.Contract;
};

/** Public Sepolia RPC fallback so the dapp works out-of-the-box without setup. */
export const PUBLIC_SEPOLIA_RPC = 'https://ethereum-sepolia-rpc.publicnode.com';

/**
 * A read-only provider that does not require a connected wallet.
 * Order of preference:
 *   1. VITE_SEPOLIA_RPC (Infura / Alchemy / private)
 *   2. window.ethereum (BrowserProvider) — only used while wallet present
 *   3. Public Sepolia RPC (publicnode.com) — last-resort, rate-limited
 */
export function getReadProvider(): ethers.Provider {
  const rpc = import.meta.env.VITE_SEPOLIA_RPC;
  if (rpc) return new ethers.JsonRpcProvider(rpc, CHAIN_ID);
  if (typeof window !== 'undefined' && window.ethereum) {
    return new ethers.BrowserProvider(window.ethereum);
  }
  return new ethers.JsonRpcProvider(PUBLIC_SEPOLIA_RPC, CHAIN_ID);
}

export function getContracts(runner: ethers.ContractRunner): MethaContracts {
  return {
    token: new ethers.Contract(
      ADDRESSES.MethaToken,
      (MethaTokenArtifact as { abi: ethers.InterfaceAbi }).abi,
      runner
    ),
    crowdsale: new ethers.Contract(
      ADDRESSES.MethaCrowdsale,
      (MethaCrowdsaleArtifact as { abi: ethers.InterfaceAbi }).abi,
      runner
    ),
    governance: new ethers.Contract(
      ADDRESSES.OpenGovernance,
      (OpenGovernanceArtifact as { abi: ethers.InterfaceAbi }).abi,
      runner
    ),
    auditingPool: new ethers.Contract(
      ADDRESSES.AuditingPool,
      (AuditingPoolArtifact as { abi: ethers.InterfaceAbi }).abi,
      runner
    ),
    registry: new ethers.Contract(
      ADDRESSES.AuditorRegistryV2,
      (AuditorRegistryV2Artifact as { abi: ethers.InterfaceAbi }).abi,
      runner
    ),
    storage: new ethers.Contract(
      ADDRESSES.EternalStorage,
      (EternalStorageArtifact as { abi: ethers.InterfaceAbi }).abi,
      runner
    ),
  };
}
